import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import InfoIcon from '@atlaskit/icon/core/status-information';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card as SmartCard } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import { DeepResearchPill } from '../../common/ui/deep-research-pill/index.tsx';
import { SiteName } from '../../common/ui/site-name/SiteName.tsx';
import { smartLinkFallback } from '../../common/ui/smart-card/index.tsx';
import {
	WebSearchPillDataComponent,
	WebSearchPillListDataComponent,
} from '../../common/ui/web-search-pill/index.tsx';
import type { AgentKnowledgeFilter } from '../../utils/agents/index.tsx';
import { useKnowledgeFilterService } from '../knowledge-filter/index.ts';
import type { KnowledgeSiteGroup } from '../knowledge-products-rendering/index.tsx';

import type { knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioListRef$key } from './__generated__/knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioListRef.graphql.ts';
import type { knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioRef$key } from './__generated__/knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioRef.graphql.ts';
import messages from './messages.ts';

type KnowledgeScope = 'org' | 'custom' | 'none';

const styles = cssMap({
	siteSources: {
		paddingInlineStart: token('space.300'),
	},
});

type ResolvedKnowledgeFilter = AgentKnowledgeFilter & {
	url: string;
};

const KnowledgeFilterLinks = ({
	filters,
	knowledgeSiteGroups,
	shouldGroupBySite,
}: {
	filters: ResolvedKnowledgeFilter[];
	knowledgeSiteGroups?: KnowledgeSiteGroup[];
	shouldGroupBySite?: boolean;
}): React.JSX.Element => {
	di(SiteName, SmartCard);

	const renderFilter = (filter: ResolvedKnowledgeFilter) => (
		<SmartCard
			key={`${filter.ari}-${filter.cloudId ?? ''}`}
			fallbackComponent={smartLinkFallback(filter.url)}
			appearance="inline"
			url={filter.url}
		/>
	);

	if (!shouldGroupBySite || !knowledgeSiteGroups?.length) {
		return <>{filters.map(renderFilter)}</>;
	}
	const filtersByAri = Object.fromEntries(filters.map((filter) => [filter.ari, filter]));

	return (
		<Stack space="space.100">
			{knowledgeSiteGroups?.map(({ cloudId, filters: siteFilters, products }) => (
				<Stack key={cloudId} space="space.100">
					<SiteName cloudId={cloudId} />
					<Box xcss={styles.siteSources}>
						<Stack space="space.050">
							{siteFilters.flatMap((filter) => {
								const resolvedFilter = filtersByAri[filter.ari];
								return resolvedFilter ? [renderFilter(resolvedFilter)] : [];
							})}
							{products}
						</Stack>
					</Box>
				</Stack>
			))}
		</Stack>
	);
};

/**
 * This component should only take either list of scenarios OR a single scenario
 * This is due to some knowledge pills (like web search) that has specific logic depending on if it's a list of scenarios or a single scenario
 */
type KnowledgeSectionScenarioProps =
	| {
			scenarioListRef: knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioListRef$key | null;
			scenarioRef?: never;
	  }
	| {
			scenarioListRef?: never;
			scenarioRef: knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioRef$key | null;
	  }
	| {
			scenarioListRef?: never;
			scenarioRef?: never;
	  };

type KnowledgeSectionProps = {
	/** @deprecated Use knowledgeFilters. Remove after rovo_agents_unit_scoped cleanup. */
	aris?: string[];
	currentCloudId?: string;
	isForgeAgent: boolean;
	knowledgeFilters?: AgentKnowledgeFilter[];
	knowledgeSiteGroups?: KnowledgeSiteGroup[];
	knowledgeProductsRender: React.ReactNode | null;
	knowledgeScopeEnabled: boolean;
	shouldGroupKnowledgeBySite?: boolean;
	isDeepResearchEnabled?: boolean;
} & KnowledgeSectionScenarioProps;

export const AgentKnowledgeSection = ({
	aris,
	currentCloudId,
	isForgeAgent,
	knowledgeFilters,
	knowledgeSiteGroups,
	knowledgeProductsRender,
	knowledgeScopeEnabled,
	shouldGroupKnowledgeBySite,
	isDeepResearchEnabled,
	scenarioListRef,
	scenarioRef,
}: KnowledgeSectionProps): React.JSX.Element => {
	di(useKnowledgeFilterService, SmartCard);

	const { formatMessage } = useIntl();
	const { getEntitiesByAris } = useKnowledgeFilterService();
	const shouldGroupKnowledge = Boolean(shouldGroupKnowledgeBySite && currentCloudId);
	const filterAris = useMemo(
		() =>
			fg('rovo_agents_unit_scoped')
				? (knowledgeFilters?.map(({ ari }) => ari) ?? [])
				: (aris ?? []),
		[aris, knowledgeFilters],
	);

	const scenarioListFragment =
		useFragment<knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioListRef$key>(
			graphql`
				fragment knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioListRef on AgentStudioScenariosResult {
					...webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillListDataComponent_scenarioListRef
				}
			`,
			scenarioListRef,
		);

	const scenarioFragment =
		useFragment<knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioRef$key>(
			graphql`
				fragment knowledgeSection_AtlassianConversationAssistantAgent_AgentKnowledgeSection_scenarioRef on AgentStudioScenario {
					...webSearchPill_AtlassianConversationAssistantAgent_WebSearchPillDataComponent_scenarioRef
				}
			`,
			scenarioRef,
		);

	const { isFetching, data: entities } = useQuery({
		queryKey: ['getEntitiesByAris'].concat(filterAris),
		queryFn: async () => {
			if (!filterAris.length) {
				return [];
			}

			return getEntitiesByAris({
				aris: filterAris,
			});
		},
		enabled: !isForgeAgent,
	});

	const resolvedKnowledgeFilters = useMemo(() => {
		const filters: AgentKnowledgeFilter[] = fg('rovo_agents_unit_scoped')
			? (knowledgeFilters ?? [])
			: filterAris.map((ari) => ({ ari }));
		const entitiesByAri = new Map(entities?.map((entity) => [entity.ari, entity]));

		return filters.flatMap((filter) => {
			const entity = entitiesByAri.get(filter.ari);
			return entity ? [{ ...filter, url: entity.url }] : [];
		});
	}, [entities, filterAris, knowledgeFilters]);

	const knowledgeScope = useMemo<KnowledgeScope>(() => {
		if (!knowledgeScopeEnabled) {
			return 'org';
		}
		if (resolvedKnowledgeFilters.length > 0) {
			return 'custom';
		}
		return 'none';
	}, [knowledgeScopeEnabled, resolvedKnowledgeFilters]);

	const getKnowledgeRender = () => {
		if (isForgeAgent) {
			return (
				<Inline space="space.100">
					<InfoIcon color="currentColor" spacing="spacious" label="" />
					<Stack space="space.100">
						<Text size="small">{formatMessage(messages.forgeKnowledgeDescription)}</Text>
					</Stack>
				</Inline>
			);
		}

		if (knowledgeScope === 'org' && !isFetching) {
			return formatMessage(messages.allKnowledgeAdded);
		}

		if (
			knowledgeScope === 'none' &&
			!knowledgeProductsRender &&
			!knowledgeSiteGroups?.length &&
			!isFetching
		) {
			return formatMessage(messages.noKnowledgeAdded);
		}

		return (
			<Stack space="space.050">
				{isFetching ? (
					<>
						<Skeleton height={22} width={200} borderRadius={token('radius.large')} isShimmering />
						<Skeleton height={22} width={150} borderRadius={token('radius.large')} isShimmering />
						<Skeleton height={22} width={200} borderRadius={token('radius.large')} isShimmering />
					</>
				) : (
					<KnowledgeFilterLinks
						filters={resolvedKnowledgeFilters}
						knowledgeSiteGroups={knowledgeSiteGroups}
						shouldGroupBySite={shouldGroupKnowledge}
					/>
				)}
				{knowledgeProductsRender}
			</Stack>
		);
	};

	return (
		<SmartCardProvider>
			<Box testId="view-agent-knowledge-cards">
				<Stack space="space.050">
					{getKnowledgeRender()}
					{/* TODO move deep research to the same pattern as websearch, using relay fragment */}
					{isDeepResearchEnabled && fg('rovo_agent_default_scenario_deep_research_validate') && (
						<DeepResearchPill />
					)}
					{scenarioListFragment && (
						<WebSearchPillListDataComponent scenarioListRef={scenarioListFragment} />
					)}
					{scenarioFragment && <WebSearchPillDataComponent scenarioRef={scenarioFragment} />}
				</Stack>
			</Box>
		</SmartCardProvider>
	);
};
